import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Label } from '@/ui-components/ui/label';
import { Button } from '@/ui-components/ui/button';
import { Input } from '@/ui-components/ui/input';
import { Slider } from '@/ui-components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';
import { Eclipse, PaintBucket, Plus, Settings2, Trash2 } from 'lucide-react';
import { ColorValue, Fill } from '../../../core/template.schemas';
import { resolveStaticOrDynamicWithPreview, ResolveContext } from '../../../core/runtime/resolve';
import { AdvancedStaticDynamicSelector } from '../content-selector/AdvancedStaticDynamicSelector';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { cn } from '@/lib/utils/cn';

interface FillSelectorProps {
  value?: Fill;
  onChange: (value: Fill) => void;
  onOpenSlotPanel?: () => void;
}

const DEFAULT_SOLID: Fill = { kind: 'solid', color: { mode: 'static', mediaType: 'color', staticValue: '#000000' }, opacity: 1 };
const DEFAULT_GRADIENT: Fill = {
  kind: 'gradient',
  gradientType: 'linear',
  angle: 90,
  stops: [
    { offset: 0, color: { mode: 'static', mediaType: 'color', staticValue: '#ffffff' }, opacity: 1 },
    { offset: 1, color: { mode: 'static', mediaType: 'color', staticValue: '#000000' }, opacity: 1 },
  ],
};

export const FillSelector: React.FC<FillSelectorProps> = ({ 
  value = DEFAULT_SOLID, 
  onChange,
  onOpenSlotPanel 
}) => {
  const slots = useTemplateStore(s => s.template.slots);
  const resolveContext = useMemo<ResolveContext>(() => ({
    selected: {},
    overrides: {},
    templateSlots: slots
  }), [slots]);

  // Determine current mode (solid or gradient)
  // Check 'kind' first, fallback to checking props for legacy data
  const mode = value.kind === 'gradient' ? 'gradient' : 'solid';
  const [isGradientPopoverOpen, setIsGradientPopoverOpen] = useState(false);

  const handleModeChange = (newMode: 'solid' | 'gradient') => {
    if (newMode === mode) return;
    onChange(newMode === 'solid' ? DEFAULT_SOLID : DEFAULT_GRADIENT);
  };

  const updateSolidColor = (color: ColorValue) => {
    const currentOpacity = value.kind === 'solid' ? value.opacity : 1;
    onChange({ kind: 'solid', color, opacity: currentOpacity });
  };
  
  const updateSolidOpacity = (opacity: number) => {
    const currentColor = value.kind === 'solid' ? value.color : DEFAULT_SOLID.color;
    onChange({ kind: 'solid', color: currentColor, opacity });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PaintBucket className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Füllung</Label>
        </div>
        <div className="flex items-center bg-gray-100/80 p-0.5 rounded-lg border border-gray-200">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleModeChange('solid')}
            className={cn(
              "h-6 px-2 text-[10px] gap-1.5 rounded-md transition-all",
              mode === 'solid' 
                ? "bg-white text-blue-600 shadow-sm font-medium" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <PaintBucket className={cn("w-3 h-3", mode === 'solid' ? "text-blue-600" : "text-gray-400")} />
            Einfarbig
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleModeChange('gradient')}
            className={cn(
              "h-6 px-2 text-[10px] gap-1.5 rounded-md transition-all",
              mode === 'gradient' 
                ? "bg-white text-blue-600 shadow-sm font-medium" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <Eclipse className={cn("w-3 h-3", mode === 'gradient' ? "text-blue-600" : "text-gray-400")} />
            Verlauf
          </Button>
        </div>
      </div>

      {mode === 'solid' ? (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <AdvancedStaticDynamicSelector
            label="Farbe"
            value={(value as any).color || DEFAULT_SOLID.color}
            onChange={(color) => updateSolidColor(color as any)}
            compatibleMediaTypes={['color']}
            onOpenSlotPanel={onOpenSlotPanel}
            required
          />
          <div className="space-y-2.5 pt-1">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-gray-500 font-medium">Deckkraft</Label>
              <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">
                {Math.round(((value as any).opacity ?? 1) * 100)}%
              </span>
            </div>
            <Slider
              value={[(value as any).opacity ?? 1]}
              min={0}
              max={1}
              step={0.01}
              onValueChange={([val]) => updateSolidOpacity(val)}
              className="w-full"
            />
          </div>
        </div>
      ) : (
        <GradientPopup
          value={value as any}
          onChange={(g) => onChange({ ...g, kind: 'gradient' })}
          onOpenSlotPanel={onOpenSlotPanel}
          open={isGradientPopoverOpen}
          onOpenChange={setIsGradientPopoverOpen}
          resolveContext={resolveContext}
        />
      )}
    </div>
  );
};

// --- Gradient Editor Subcomponent ---

interface GradientEditorProps {
  value: any; // GradientFill
  onChange: (value: any) => void;
  onOpenSlotPanel?: () => void;
  resolveContext: ResolveContext;
}

const colorToRgba = (hexOrRgba: string, opacity: number) => {
  if (hexOrRgba?.startsWith('rgba')) return hexOrRgba;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hexOrRgba)) return hexOrRgba;
  const hex = hexOrRgba.length === 4
    ? `#${hexOrRgba[1]}${hexOrRgba[1]}${hexOrRgba[2]}${hexOrRgba[2]}${hexOrRgba[3]}${hexOrRgba[3]}`
    : hexOrRgba;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const buildGradientPreview = (value: any, context: ResolveContext = { selected: {}, overrides: {} }) => {
  const stops = Array.isArray(value?.stops) ? value.stops : [];
  const resolvedStops = stops
    .slice()
    .sort((a: any, b: any) => (a?.offset ?? 0) - (b?.offset ?? 0))
    .map((s: any) => {
      const baseColor = resolveStaticOrDynamicWithPreview(s.color, context);
      const withOpacity = (s?.opacity ?? 1) < 1 ? colorToRgba(baseColor as any, s.opacity ?? 1) : baseColor;
      return `${withOpacity} ${(Math.max(0, Math.min(1, s?.offset ?? 0)) * 100).toFixed(1)}%`;
    })
    .join(', ');

  if (value?.gradientType === 'radial') {
    return `radial-gradient(circle farthest-side, ${resolvedStops || 'rgba(148, 163, 184, 0.4) 0%, rgba(148, 163, 184, 0.9) 100%'})`;
  }

  const angle = Number.isFinite(Number(value?.angle)) ? Number(value.angle) : 90;
  return `linear-gradient(${angle}deg, ${resolvedStops || 'rgba(148, 163, 184, 0.4) 0%, rgba(148, 163, 184, 0.9) 100%'})`;
};

const GradientPopup: React.FC<
  GradientEditorProps & { open: boolean; onOpenChange: (open: boolean) => void }
> = ({ value, onChange, onOpenSlotPanel, open, onOpenChange, resolveContext }) => {
  const stopsCount = Array.isArray(value?.stops) ? value.stops.length : 0;
  const preview = useMemo(() => buildGradientPreview(value, resolveContext), [value, resolveContext]);
  const angleText = value?.gradientType === 'linear' ? `${Number.isFinite(Number(value?.angle)) ? Number(value.angle) : 90}°` : '—';
  const typeText = value?.gradientType === 'radial' ? 'Radial' : 'Linear';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-gray-300">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-8 w-12 rounded-lg border border-gray-100 shadow-inner shrink-0 relative overflow-hidden"
            style={{ background: preview }}
          >
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-gray-800 truncate leading-tight">{typeText} Verlauf</div>
            <div className="text-[10px] text-gray-500 font-medium">{stopsCount} Stopps · {angleText}</div>
          </div>
        </div>

        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0 rounded-lg hover:bg-gray-50 border-gray-200">
              <Settings2 className="w-3.5 h-3.5 text-gray-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="left" align="start" className="w-80 p-4 z-50 rounded-2xl shadow-xl border-gray-100">
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-gray-50 pb-2 mb-1">
                <div className="flex items-center gap-2">
                  <Eclipse className="w-3.5 h-3.5 text-blue-600" />
                  <Label className="text-[12px] font-bold text-gray-800">Verlauf bearbeiten</Label>
                </div>
              </div>
              <GradientEditor value={value} onChange={onChange} onOpenSlotPanel={onOpenSlotPanel} resolveContext={resolveContext} />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

const GradientEditor: React.FC<GradientEditorProps> = ({ value, onChange, onOpenSlotPanel, resolveContext }) => {
  const [selectedStopIndex, setSelectedStopIndex] = useState<number>(0);
  const trackRef = useRef<HTMLDivElement>(null);

  // Ensure stops are sorted by offset for rendering, but we keep original order or manage index carefully.
  // Actually, let's keep them stable.
  // Ensure stops are sorted by offset for rendering, but we keep original order or manage index carefully.
  // Actually, let's keep them stable.
  const stops = useMemo(() => value.stops || [], [value.stops]);

  const handleStopAdd = () => {
    // Add a stop in the middle
    const newStop = { 
        offset: 0.5, 
        color: { mode: 'static', mediaType: 'color', staticValue: '#888888' }, 
        opacity: 1 
    };
    const newStops = [...stops, newStop];
    // Sort? Word doesn't sort automatically while dragging but usually data structures expects sorted for rendering.
    // Let's sort to keep it simple.
    newStops.sort((a: any, b: any) => a.offset - b.offset);
    onChange({ ...value, stops: newStops });
    setSelectedStopIndex(newStops.indexOf(newStop));
  };

  const handleStopDelete = () => {
    if (stops.length <= 2) return; // Min 2 stops
    const newStops = stops.filter((_: any, i: number) => i !== selectedStopIndex);
    onChange({ ...value, stops: newStops });
    setSelectedStopIndex(Math.max(0, selectedStopIndex - 1));
  };

  const updateStopCallback = useCallback((index: number, updates: any) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], ...updates };
    
    if (updates.offset !== undefined) {
        const currentStop = newStops[index];
        newStops.sort((a: any, b: any) => a.offset - b.offset);
        const newIndex = newStops.indexOf(currentStop);
        setSelectedStopIndex(newIndex);
    }
    onChange({ ...value, stops: newStops });
  }, [stops, onChange, value]);

  const handleTrackClick = (e: React.MouseEvent) => {
     if (e.target !== trackRef.current) return; // Only click on track, not handles
     const rect = trackRef.current.getBoundingClientRect();
     const x = e.clientX - rect.left;
     const offset = Math.max(0, Math.min(1, x / rect.width));
     
     // Add stop at this offset
     const newStop = { 
        offset, 
        color: { mode: 'static', mediaType: 'color', staticValue: '#888888' }, 
        opacity: 1 
    };
    const newStops = [...stops, newStop];
    newStops.sort((a: any, b: any) => a.offset - b.offset);
    onChange({ ...value, stops: newStops });
    setSelectedStopIndex(newStops.indexOf(newStop));
  };

  // Helper to generate CSS gradient preview string
  const cssGradient = useMemo(() => {
    return buildGradientPreview(value, resolveContext);
  }, [value, resolveContext]);

  const selectedStop = stops[selectedStopIndex];

  return (
    <div className="space-y-4">
      {/* Type & Angle */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
             <Label className="text-[11px] text-gray-500 font-medium">Typ</Label>
             <Select 
                value={value.gradientType} 
                onValueChange={(v) => onChange({ ...value, gradientType: v })}
             >
                <SelectTrigger className="h-8 text-[11px] rounded-lg border-gray-200">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                    <SelectItem value="linear" className="text-[11px]">Linear</SelectItem>
                    <SelectItem value="radial" className="text-[11px]">Radial</SelectItem>
                </SelectContent>
             </Select>
        </div>
        {value.gradientType === 'linear' && (
             <div className="space-y-1.5">
                <Label className="text-[11px] text-gray-500 font-medium">Winkel</Label>
                <div className="flex items-center gap-2 relative">
                     <Input 
                        type="number" 
                        value={value.angle ?? 90} 
                        onChange={(e) => onChange({ ...value, angle: Number(e.target.value) })}
                        className="h-8 text-[11px] w-full rounded-lg border-gray-200 pr-6"
                     />
                     <span className="text-[10px] text-gray-400 absolute right-2 font-medium">°</span>
                </div>
            </div>
        )}
      </div>

      {/* Gradient Slider */}
      <div className="space-y-2.5">
         <div className="flex justify-between items-center bg-gray-50/50 p-1 pl-2.5 rounded-xl border border-gray-100">
             <Label className="text-[11px] font-bold text-gray-600">Stopps</Label>
             <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md hover:bg-white hover:shadow-sm transition-all" onClick={handleStopAdd} title="Stopp hinzufügen">
                    <Plus className="w-3 h-3 text-gray-500" />
                </Button>
                 <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 rounded-md text-red-400 hover:text-red-500 hover:bg-white hover:shadow-sm transition-all" 
                    onClick={handleStopDelete} 
                    disabled={stops.length <= 2}
                    title="Stopp löschen"
                 >
                    <Trash2 className="w-3 h-3" />
                </Button>
             </div>
         </div>
         
         <div className="relative h-14 flex items-center justify-center select-none pt-4">
             {/* Slider Track */}
             <div 
                ref={trackRef}
                className="w-full h-5 rounded-full shadow-inner border border-gray-100 cursor-crosshair relative overflow-hidden" 
                style={{ background: cssGradient }}
                onClick={handleTrackClick}
             >
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none" />
             </div>
             
             {/* Handles */}
             {stops.map((stop: any, index: number) => (
                 <div
                    key={index}
                    className={cn(
                        "absolute top-4 w-5 h-8 -ml-2.5 cursor-grab active:cursor-grabbing hover:z-10 transition-transform",
                        selectedStopIndex === index ? "z-20 scale-110" : "z-0"
                    )}
                    style={{ left: `${stop.offset * 100}%` }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        setSelectedStopIndex(index);
                        
                        // Simple Drag Implementation
                        const startX = e.clientX;
                        const track = trackRef.current;
                        if(!track) return;
                        const rect = track.getBoundingClientRect();
                        const startOffset = stop.offset;

                        const moveHandler = (moveEvent: MouseEvent) => {
                            const dx = moveEvent.clientX - startX;
                            const dOffset = dx / rect.width;
                            let newOffset = Math.max(0, Math.min(1, startOffset + dOffset));
                            updateStopCallback(index, { offset: newOffset });
                        };

                        const upHandler = () => {
                            window.removeEventListener('mousemove', moveHandler);
                            window.removeEventListener('mouseup', upHandler);
                        };
                        window.addEventListener('mousemove', moveHandler);
                        window.addEventListener('mouseup', upHandler);
                    }}
                >
                    {/* Visual Handle Shape (Modern Pin) */}
                    <div className={cn(
                        "w-5 h-5 mt-4 border-2 bg-white flex items-center justify-center shadow-md transform rotate-45 rounded-tl-full rounded-tr-full rounded-bl-full transition-colors",
                        selectedStopIndex === index ? "border-blue-500 shadow-blue-200" : "border-gray-200"
                    )}>
                        <div 
                            className="w-full h-full bg-current rounded-full"
                            style={{ 
                                backgroundColor: resolveStaticOrDynamicWithPreview(stop.color, resolveContext)
                            }} 
                           />
                    </div>
                 </div>
             ))}
         </div>
      </div>

      {/* Selected Stop Properties */}
      {selectedStop && (
          <div className="space-y-4 pt-4 border-t border-gray-50 animate-in fade-in slide-in-from-top-1">
             <div className="flex items-center gap-2">
                <div className="w-1 h-3.5 bg-blue-500 rounded-full" />
                <Label className="text-[12px] font-bold text-gray-800">Ausgewählter Stopp</Label>
             </div>
             
             <div className="grid grid-cols-[1fr,auto] gap-4 items-end">
                 <div className="space-y-1">
                     <AdvancedStaticDynamicSelector
                        label="Farbe"
                        value={selectedStop.color}
                        onChange={(val) => updateStopCallback(selectedStopIndex, { color: val })}
                        compatibleMediaTypes={['color']}
                        onOpenSlotPanel={onOpenSlotPanel}
                        required
                    />
                 </div>
                 <div className="space-y-1.5 w-24">
                     <Label className="text-[11px] text-gray-500 font-medium">Position</Label>
                     <div className="relative">
                        <Input 
                            type="number" 
                            min={0} max={100} 
                            value={Math.round(selectedStop.offset * 100)} 
                            onChange={(e) => updateStopCallback(selectedStopIndex, { offset: Number(e.target.value) / 100 })}
                            className="h-8 text-[11px] rounded-lg border-gray-200 pr-6"
                        />
                        <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">%</span>
                     </div>
                 </div>
             </div>

             <div className="space-y-2.5">
                 <div className="flex justify-between items-center">
                    <Label className="text-[11px] text-gray-500 font-medium">Deckkraft</Label>
                    <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">
                      {Math.round((selectedStop.opacity ?? 1) * 100)}%
                    </span>
                 </div>
                 <Slider
                    value={[(selectedStop.opacity ?? 1)]}
                    min={0}
                    max={1}
                    step={0.01}
                    onValueChange={([val]) => updateStopCallback(selectedStopIndex, { opacity: val })}
                 />
             </div>
          </div>
      )}
    </div>
  );
};
